import { addDays } from '~/calculations/calendar'
import { type ProjectSummary, summariseProject } from '~/calculations/projectSummary'
import { serializeCompletionSummary } from '~/exports/json/completionSummary'
import type { PrivacySelection } from '~/exports/scope'
import type { PhotoRecord } from '~/models/photo'
import { type ProjectRecord, type ProjectStatus, projectHasStarted } from '~/models/project'
import type { SolaDatabase } from '~/storage/db'
import { type StorageError, storageError, toStorageError } from '~/storage/errors'
import { notifyWritten } from '~/storage/invalidation'
import { listMeasurementSessions } from '~/storage/repositories/measurementRepository'
import { listPhases } from '~/storage/repositories/phaseRepository'
import { getActiveProject, listProjects } from '~/storage/repositories/projectRepository'
import { listWeeklyReviews } from '~/storage/repositories/weeklyReviewRepository'
import { listWeightEntriesInRange } from '~/storage/repositories/weightRepository'
import { STORE } from '~/storage/schema'
import { withTransaction } from '~/storage/transactions'
import { type CalendarDate, toCalendarDate, toIsoTimestamp, unwrapDate } from '~/types/dates'
import type { ProjectId } from '~/types/ids'
import { err, ok, type Result } from '~/utils/result'
import { todayFor } from './weightService'

/**
 * Project lifecycle (D-062, D-020).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  A PROJECT ENDS ONLY BY EXPLICIT DECISION, AND NEVER STARTS THE NEXT ONE.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Reaching the target date warns and changes nothing. There is no scheduler, no
 * "auto-complete on load", and no branch anywhere that ends a project because a
 * date passed — this is D-020 applied one level up from the phase, and the
 * absence is the feature.
 *
 * A completion may **suggest** a next project. It must never create or start
 * one: that is a separate decision, made separately, by someone who has read
 * the summary of the one that just finished.
 */

export interface ProjectEndState {
  readonly project: ProjectRecord | undefined
  /** True once today is on or after the target date. */
  readonly reachedTargetDate: boolean
  readonly daysPastTargetDate: number
}

export async function getProjectEndState(
  db: SolaDatabase,
  now: Date,
): Promise<Result<ProjectEndState, StorageError>> {
  const active = await getActiveProject(db)
  if (!active.ok) return active

  const project = active.value
  if (project === undefined) {
    return ok({ project: undefined, reachedTargetDate: false, daysPastTargetDate: 0 })
  }

  const today = unwrapDate(todayFor(now))
  const target = unwrapDate(project.targetDate)
  // Lexical comparison is exact for zero-padded YYYY-MM-DD.
  const reached = today >= target

  return ok({
    project,
    reachedTargetDate: reached,
    daysPastTargetDate: reached ? daysBetweenIso(target, today) : 0,
  })
}

/**
 * Where a project's evidence window opens (D-107 §3).
 *
 * `createdOn` when the project was set up before it started, `startDate`
 * otherwise — which is every record written before that field existed, and is
 * why it needs no migration. **Never pass this to `summariseProject` as the
 * project's start**; see the comment at the call site.
 */
function evidenceWindowStart(project: ProjectRecord): CalendarDate {
  return project.createdOn ?? project.startDate
}

/**
 * How far ahead a waiting project may be rescheduled (D-107 §7).
 *
 * The same seven days the setup wizard enforces, for the same reason: the route
 * came from the measurement taken at setup and must not be stale when it begins.
 * Declared here rather than imported from `projectSetupService`, which imports
 * this module — and duplicated deliberately would be worse, so the wizard's
 * `MAX_START_DATE_DAYS_AHEAD` re-exports this one.
 */
export const MAX_PROJECT_START_DAYS_AHEAD = 7

export interface ProjectStartState {
  readonly project: ProjectRecord | undefined
  /** False while the project exists and has not begun — the waiting state. */
  readonly hasStarted: boolean
  /** The day it begins. Present whenever a project is loaded, started or not. */
  readonly startsOn: CalendarDate | undefined
  /** Zero once it has started. */
  readonly daysUntilStart: number
}

/**
 * Has the active project begun, and if not, when does it? (D-107 §2, §4.)
 *
 * The single impure reader of the waiting state, mirroring `getProjectEndState`
 * above deliberately. **Every screen and service goes through this** — no caller
 * re-derives the comparison, for the reason `projectHasStarted` documents.
 *
 * A waiting project executes nothing: no materialised training week, no week
 * checklist, no review draft, no phase-end notice, no opinion. Recording still
 * works in full, because the user is living those days and the evidence is real.
 */
export async function getProjectStartState(
  db: SolaDatabase,
  now: Date,
): Promise<Result<ProjectStartState, StorageError>> {
  const active = await getActiveProject(db)
  if (!active.ok) return active

  const project = active.value
  if (project === undefined) {
    return ok({ project: undefined, hasStarted: true, startsOn: undefined, daysUntilStart: 0 })
  }

  const today = todayFor(now)
  const hasStarted = projectHasStarted(project, today)

  return ok({
    project,
    hasStarted,
    startsOn: project.startDate,
    daysUntilStart: hasStarted
      ? 0
      : daysBetweenIso(unwrapDate(today), unwrapDate(project.startDate)),
  })
}

function daysBetweenIso(from: string, to: string): number {
  return Math.round(
    (Date.parse(`${to}T00:00:00.000Z`) - Date.parse(`${from}T00:00:00.000Z`)) / 86_400_000,
  )
}

/**
 * Extend the project — the first of the three explicit decisions.
 *
 * Moves the target date and leaves everything else alone. This is why `extend`
 * is not a `ProjectStatus`: the project has not ended, so there is no ending to
 * record.
 */
export async function extendProject(
  db: SolaDatabase,
  id: ProjectId,
  targetDate: string,
  now: Date,
): Promise<Result<ProjectRecord, StorageError>> {
  const parsed = toCalendarDate(targetDate)
  if (parsed === undefined) {
    return err(storageError('invalidRecord', 'Target date must be a real date in YYYY-MM-DD form.'))
  }

  const existing = await readProject(db, id)
  if (!existing.ok) return existing

  if (existing.value.status !== 'active') {
    return err(
      storageError('invalidRecord', 'A project that has already ended cannot be extended.'),
    )
  }
  if (unwrapDate(parsed) < unwrapDate(existing.value.startDate)) {
    return err(storageError('invalidRecord', 'The target date must not precede the project start.'))
  }

  return writeProject(db, {
    ...existing.value,
    targetDate: parsed,
    updatedAt: toIsoTimestamp(now),
  })
}

export interface EndProjectRequest {
  /** `completed` or `abandoned` — the two ways a project actually ends. */
  readonly status: Exclude<ProjectStatus, 'active'>
  readonly endedOn: string
  /** Why. Recorded rather than inferred, especially when abandoning. */
  readonly reason?: string
}

/**
 * End the project, with its summary written in the same act.
 *
 * The summary is built from the records as they stand at this moment and stored
 * on the project, not recomputed on every later view. A finished project is
 * long-term body history; recomputing it would run today's counting over data
 * the user has since edited and report something they never read (D-046's
 * reasoning, one level up).
 *
 * **Nothing is started.** The caller may suggest a next project to the user;
 * this function creates nothing (D-020).
 */
export async function endProject(
  db: SolaDatabase,
  id: ProjectId,
  request: EndProjectRequest,
  now: Date,
): Promise<Result<ProjectRecord, StorageError>> {
  const endedOn = toCalendarDate(request.endedOn)
  if (endedOn === undefined) {
    return err(storageError('invalidRecord', 'End date must be a real date in YYYY-MM-DD form.'))
  }

  const existing = await readProject(db, id)
  if (!existing.ok) return existing

  if (existing.value.status !== 'active') {
    return err(storageError('invalidRecord', 'This project has already ended.'))
  }
  if (unwrapDate(endedOn) < unwrapDate(existing.value.startDate)) {
    return err(storageError('invalidRecord', 'The end date must not precede the project start.'))
  }

  const summary = await buildProjectSummary(db, existing.value, endedOn)
  if (!summary.ok) return summary

  return writeProject(db, {
    ...existing.value,
    status: request.status,
    endedOn,
    ...(request.reason === undefined ? {} : { endReason: request.reason }),
    completionSummary: summary.value,
    updatedAt: toIsoTimestamp(now),
  })
}

/**
 * Move the start date of a project that has not begun (D-107 §5).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THE PHASE AND THE GOAL MOVE WITH IT, IN THE SAME TRANSACTION.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Moving only the project leaves the phase starting on a different day, and
 * D-034 makes the phase's first day week 1 day 1 — so the project's own start
 * and its week numbering would disagree for the whole life of the project,
 * silently and with nothing on any screen to show it. **Test the three records
 * together, never the project alone.**
 *
 * **This is not a phase correction and must not be routed through one.** D-070's
 * corrections record a mistake about a phase that ran; this phase has not run, so
 * there is nothing to correct and appending one would put a fiction in the
 * history.
 *
 * Frozen once the project begins. After that the phase's own start is editable
 * only through D-070, which is the path that records why.
 */
export async function rescheduleProjectStart(
  db: SolaDatabase,
  id: ProjectId,
  startDate: string,
  now: Date,
): Promise<Result<ProjectRecord, StorageError>> {
  const parsed = toCalendarDate(startDate)
  if (parsed === undefined) {
    return err(storageError('invalidRecord', 'Start date must be a real date in YYYY-MM-DD form.'))
  }

  const existing = await readProject(db, id)
  if (!existing.ok) return existing

  const project = existing.value
  const today = todayFor(now)

  if (project.status !== 'active') {
    return err(storageError('invalidRecord', 'A project that has ended cannot be rescheduled.'))
  }
  if (projectHasStarted(project, today)) {
    return err(
      storageError('invalidRecord', 'This project has already started, so its start date is set.'),
    )
  }
  if (unwrapDate(parsed) < unwrapDate(today)) {
    return err(storageError('invalidRecord', 'The start date must not be in the past.'))
  }
  if (unwrapDate(parsed) > unwrapDate(addDays(today, MAX_PROJECT_START_DAYS_AHEAD))) {
    return err(
      storageError(
        'invalidRecord',
        'A project can start at most a week from now, because the route came from the measurements you recorded.',
      ),
    )
  }
  if (unwrapDate(parsed) > unwrapDate(project.targetDate)) {
    return err(
      storageError(
        'invalidRecord',
        'The start date must not be after the date you are aiming for.',
      ),
    )
  }

  const timestamp = toIsoTimestamp(now)
  const record: ProjectRecord = { ...project, startDate: parsed, updatedAt: timestamp }

  const written = await withTransaction(
    db,
    [STORE.projects, STORE.phases, STORE.goals] as const,
    async (tx) => {
      await tx.objectStore(STORE.projects).put(record)

      /* Both are scoped by `projectId` and a waiting project has at most one of
         each, but they are moved by iteration rather than by a remembered id —
         a project whose phase was created by a path this function does not know
         about must still end up consistent. */
      const phases = await tx.objectStore(STORE.phases).index('projectId').getAll(id)
      for (const phase of phases) {
        await tx
          .objectStore(STORE.phases)
          .put({ ...phase, startDate: parsed, updatedAt: timestamp })
      }

      const goals = await tx.objectStore(STORE.goals).index('projectId').getAll(id)
      for (const goal of goals) {
        await tx.objectStore(STORE.goals).put({ ...goal, startDate: parsed, updatedAt: timestamp })
      }
    },
  )
  if (!written.ok) return err(written.error)

  notifyWritten(STORE.projects)
  notifyWritten(STORE.phases)
  notifyWritten(STORE.goals)
  return ok(record)
}

/**
 * Discard a project that has not begun (D-107 §6).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THE PLAN GOES. THE OBSERVATIONS STAY.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Removed: the project, its phase, its body-composition target, its domain goals
 * and its nutrition plans — all of it plan structure the user is abandoning.
 *
 * Kept: the measurement session, the weigh-in and the photos, with their
 * `phaseId` and `projectId` cleared. They are the user's own observations of
 * their own body, and a project that never ran has no claim on them. This is
 * cheap only because both fields are already optional on both records; had
 * either been required this would have needed a schema change and D-107's
 * no-migration property would have collapsed.
 *
 * **A started project may not be discarded** — that is what ending one is for,
 * and an ending writes a summary because there is something to summarise.
 */
export async function discardWaitingProject(
  db: SolaDatabase,
  id: ProjectId,
  now: Date,
): Promise<Result<void, StorageError>> {
  const existing = await readProject(db, id)
  if (!existing.ok) return existing

  const project = existing.value
  if (project.status !== 'active') {
    return err(storageError('invalidRecord', 'This project has already ended.'))
  }
  if (projectHasStarted(project, todayFor(now))) {
    return err(
      storageError(
        'invalidRecord',
        'This project has already started. End it in Settings rather than discarding it.',
      ),
    )
  }

  const timestamp = toIsoTimestamp(now)
  const written = await withTransaction(
    db,
    [
      STORE.projects,
      STORE.phases,
      STORE.goals,
      STORE.domainGoals,
      STORE.nutritionPlans,
      STORE.trainingWeeks,
      STORE.measurementSessions,
      STORE.photos,
    ] as const,
    async (tx) => {
      /* The phases are collected separately from the other three because their
         ids are needed below: a measurement session and a photo point at a phase
         rather than at a project, so unlinking them means knowing which phases
         belonged to this project. */
      const phaseIds = new Set<string>()
      const phases = await tx.objectStore(STORE.phases).index('projectId').getAll(id)
      for (const phase of phases) {
        phaseIds.add(phase.id)
        await tx.objectStore(STORE.phases).delete(phase.id)
      }

      for (const store of [STORE.goals, STORE.domainGoals, STORE.nutritionPlans] as const) {
        const owned = await tx.objectStore(store).index('projectId').getAll(id)
        for (const record of owned) {
          await tx.objectStore(store).delete(record.id)
        }
      }

      /* None can exist — `materialiseCurrentWeek` refuses before a project starts
         (D-107 §4) — but a record pointing at a deleted project would be worse
         than a redundant delete. The test asserts the count is zero in the
         ordinary case, so this cannot quietly hide a regression in that guard. */
      const weeks = await tx.objectStore(STORE.trainingWeeks).index('projectId').getAll(id)
      for (const week of weeks) {
        await tx.objectStore(STORE.trainingWeeks).delete(week.id)
      }

      /* Unlinked, not deleted. The user did stand on the scale and in front of
         the camera; a discarded plan does not un-happen that. */
      const sessions = await tx.objectStore(STORE.measurementSessions).getAll()
      for (const session of sessions) {
        if (session.phaseId === undefined || !phaseIds.has(session.phaseId)) continue
        const { phaseId: _dropped, ...rest } = session
        await tx.objectStore(STORE.measurementSessions).put({ ...rest, updatedAt: timestamp })
      }

      /* The photos store is `UnmodelledRecord` in the schema, so it is narrowed
         here exactly as `photoRepository` narrows it at its own boundary. */
      const photos = (await tx.objectStore(STORE.photos).getAll()) as unknown as PhotoRecord[]
      for (const photo of photos) {
        const ownedByPhase = photo.phaseId !== undefined && phaseIds.has(photo.phaseId)
        if (photo.projectId !== id && !ownedByPhase) continue
        const { phaseId: _phase, projectId: _project, ...rest } = photo
        await tx
          .objectStore(STORE.photos)
          .put({ ...rest, updatedAt: timestamp } as never, photo.id as never)
      }

      await tx.objectStore(STORE.projects).delete(id)
    },
  )
  if (!written.ok) return err(written.error)

  for (const store of [
    STORE.projects,
    STORE.phases,
    STORE.goals,
    STORE.domainGoals,
    STORE.nutritionPlans,
    STORE.trainingWeeks,
    STORE.measurementSessions,
    STORE.photos,
  ]) {
    notifyWritten(store)
  }
  return ok(undefined)
}

/**
 * Gather everything inside the project's span and count it.
 *
 * The counting itself is `calculations/projectSummary.ts` and is pure. This is
 * the impure half: reading the records, and deciding which of them belong to
 * the project. Membership is by `projectId` where a record carries one, and by
 * date span where it does not — weigh-ins and measurement sessions have no
 * project key, because a weigh-in belongs to a day rather than to a plan.
 */
export async function buildProjectSummary(
  db: SolaDatabase,
  project: ProjectRecord,
  endedOn: CalendarDate,
): Promise<Result<ProjectSummary, StorageError>> {
  /* The weigh-ins are fetched as a bounded key-range scan rather than read
     whole and filtered — the store is keyed by date precisely so that a span
     query is one scan (D-027). The other three have no such key and are
     filtered in memory. */
  const windowStart = evidenceWindowStart(project)
  const [weights, sessions, reviews, phases] = await Promise.all([
    listWeightEntriesInRange(db, windowStart, endedOn),
    listMeasurementSessions(db),
    listWeeklyReviews(db),
    listPhases(db),
  ])

  if (!weights.ok) return weights
  if (!sessions.ok) return sessions
  if (!reviews.ok) return reviews
  if (!phases.ok) return phases

  const from = unwrapDate(windowStart)
  const to = unwrapDate(endedOn)
  const inSpan = (date: CalendarDate): boolean => {
    const value = unwrapDate(date)
    return value >= from && value <= to
  }

  return ok(
    summariseProject({
      /* `startDate`, deliberately NOT `windowStart` (D-107 §3). `summariseProject`
         computes "Ran for N weeks" from this, and a project set up on the Thursday
         before it started did not run for that extra week. The window above is
         what evidence counts; this is how long the project lasted. Keeping them
         separate means a coverage fraction can marginally exceed 1 when the setup
         day contributed a weigh-in — `coverageOf` already handles that, and it
         reaches no screen as a ratio (D-062). Do not tidy these into one argument. */
      startDate: project.startDate,
      endDate: endedOn,
      weights: weights.value.map((entry) => ({
        date: entry.date,
        weightKg: entry.weightKg as number,
      })),
      measurements: sessions.value
        .filter((session) => inSpan(session.date))
        .map((session) => ({
          date: session.date,
          waistCm: session.sites.waistAtNavel?.medianCm,
          estimatedBodyFatPercent: session.derived.bodyFatPercent,
        })),
      /* Only completed reviews count. A draft is a week the user began and did
         not finish, and counting it would report coverage they do not have. */
      completedReviews: reviews.value.filter(
        (review) => review.projectId === project.id && review.status === 'complete',
      ).length,
      phaseCount: phases.value.filter((phase) => phase.projectId === project.id).length,
    }),
  )
}

/**
 * Build the completion-summary export for a finished project.
 *
 * A **separate, user-initiated export** (D-062). Folding a project's whole life
 * into the weekly document would make a months-long disclosure the default for
 * every AI review, and D-019's current-week-plus-four default exists to stop
 * exactly that.
 *
 * The privacy selection is passed in rather than defaulted here, so the screen
 * that offers the download is the thing that decides what leaves — and the
 * cautious defaults still apply because that is what `DEFAULT_PRIVACY_SELECTION`
 * holds.
 */
export async function renderProjectSummaryExport(
  db: SolaDatabase,
  project: ProjectRecord,
  selection: PrivacySelection,
  now: Date,
): Promise<Result<{ readonly fileName: string; readonly json: string }, StorageError>> {
  const summary = project.completionSummary
  if (summary === undefined) {
    return err(
      storageError('invalidRecord', 'That project has no summary yet, because it has not ended.'),
    )
  }

  const phases = await listPhases(db)
  if (!phases.ok) return phases

  const document = serializeCompletionSummary({
    generatedAt: toIsoTimestamp(now),
    project,
    summary,
    selection,
    phaseTypes: phases.value
      .filter((phase) => phase.projectId === project.id)
      .map((phase) => phase.type),
  })

  return ok({
    fileName: `sola-project-summary-${unwrapDate(project.endedOn ?? project.targetDate)}.json`,
    // Indented: the user may well open this before sending it on.
    json: JSON.stringify(document, null, 2),
  })
}

/** Every project, newest first. A finished one stays as long-term history. */
export async function listProjectHistory(
  db: SolaDatabase,
): Promise<Result<readonly ProjectRecord[], StorageError>> {
  return listProjects(db)
}

async function readProject(
  db: SolaDatabase,
  id: ProjectId,
): Promise<Result<ProjectRecord, StorageError>> {
  try {
    const record = await db.get(STORE.projects, id)
    if (record === undefined) {
      /* A form outliving its record says so rather than quietly creating a new
         project — the same rule the goal editor follows. */
      return err(storageError('invalidRecord', 'That project no longer exists.'))
    }
    return ok(record)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

async function writeProject(
  db: SolaDatabase,
  record: ProjectRecord,
): Promise<Result<ProjectRecord, StorageError>> {
  const written = await withTransaction(db, [STORE.projects] as const, async (tx) => {
    await tx.objectStore(STORE.projects).put(record)
  })
  if (!written.ok) return err(written.error)

  notifyWritten(STORE.projects)
  return ok(record)
}
